import axios from "axios";
import heic2any from "heic2any";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

const TestimonialCollectionDetails = () => {
  const { id } = useParams();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rating, setRating] = useState(5);
  const [source, setSource] = useState("Google");
  const [singleImage, setSingleImage] = useState(null);
  
  // Image upload states
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingSingleImage, setIsLoadingSingleImage] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  // Available testimonial sources
  const testimonialSources = [
    "Google",
    "Facebook",
    "Twitter",
    "Instagram",
    "LinkedIn",
    "Yelp",
    "Other"
  ];

  useEffect(() => {
    const fetchTestimonialDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `https://server-gs.vercel.app/admin/testimonial/${id}`
        );
        const data = response.data;

        setTestimonial(data);

        // Update state with fetched data
        if (data) {
          setTitle(data.title || "");
          setDescription(data.description || "");
          setRating(data.rating || 5);
          setSource(data.source || "Google");
          setSingleImage(data.singleImage || null);
        }
      } catch (error) {
        console.error("Failed to fetch testimonial details:", error);
        toast.error("Failed to load testimonial");
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonialDetails();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsButtonLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("rating", rating);
    formData.append("source", source);

    // Handle image upload
    if (singleImage) {
      if (singleImage instanceof File || singleImage instanceof Blob) {
        formData.append("singleImage", singleImage);
      } else if (typeof singleImage === "string") {
        formData.append("existingSingleImage", singleImage);
      }
    }

    try {
      const response = await axios.put(
        `https://server-gs.vercel.app/update-testimonial/${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      Swal.fire({
        title: "Success!",
        text: "Testimonial updated successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        // Refresh the testimonial data
        fetchTestimonialDetails();
      });
    } catch (error) {
      console.error("Error updating testimonial:", error);
      toast.error("Failed to update testimonial");
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await handleFile(file);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await handleFile(file);
    }
  };

  const handleFile = async (file) => {
    setIsLoadingSingleImage(true);
    try {
      if (
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")
      ) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });
        const convertedFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
          { type: "image/jpeg" }
        );
        setSingleImage(convertedFile);
      } else {
        setSingleImage(file);
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process image");
    } finally {
      setIsLoadingSingleImage(false);
    }
  };

  const fetchTestimonialDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://server-gs.vercel.app/admin/testimonial/${id}`
      );
      const data = response.data;
      setTestimonial(data);
      if (data) {
        setTitle(data.title || "");
        setDescription(data.description || "");
        setRating(data.rating || 5);
        setSource(data.source || "Google");
        setSingleImage(data.singleImage || null);
      }
    } catch (error) {
      console.error("Failed to fetch testimonial details:", error);
      toast.error("Failed to load testimonial");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!testimonial) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-600">No testimonial found</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-10">
      <div className="w-full py-6 px-6 border-b border-gray-200 mb-10">
        <h1 className="text-2xl font-semibold text-gray-500">
          Edit Testimonial
        </h1>
      </div>
      <div className="mx-auto w-[95%] max-w-4xl">
        <form
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          className="bg-white p-8 rounded-lg shadow-lg"
        >
          <div className="grid grid-cols-1 gap-6">
            <div className="grid grid-cols-2 gap-5">
              {/* Basic Info */}
            <div>
              <label className="block text-md font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Source Selection */}
            <div>
              <label className="block text-md font-medium text-gray-700 mb-1">
                Source <span className="text-red-500">*</span>
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {testimonialSources.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
            </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              {/* Rating Input */}
            <div>
              <label className="block text-md font-medium text-gray-700 mb-1">
                Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={rating}
                  onChange={(e) => {
                    const value = Math.min(5, Math.max(1, e.target.value));
                    setRating(value);
                  }}
                  className="block w-20 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <div className="ml-3 flex">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-6 h-6 ${
                        i < rating ? "text-yellow-400" : "text-gray-300"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>

            {/* Description Textarea */}
            <div>
              <label className="block text-md font-medium text-gray-700 mb-1">
                Testimonial <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                required
                placeholder="Share your experience..."
              />
            </div>
            </div>

            {/* Profile Image Upload */}
            <div>
              <label className="block text-md font-medium text-gray-700 mb-2">
                Profile Picture <span className="text-red-500">*</span>
              </label>
              <div
                className={`border-2 border-dashed rounded-xl p-6 h-64 flex flex-col items-center justify-center transition-all ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400 bg-gray-50"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {isLoadingSingleImage ? (
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-md text-gray-500">Uploading image...</p>
                  </div>
                ) : singleImage ? (
                  <div className="relative w-full h-full group">
                    {typeof singleImage === "string" ? (
                      <img
                        src={singleImage}
                        alt="Current Profile"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    ) : (
                      <img
                        src={URL.createObjectURL(singleImage)}
                        alt="New Profile"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setSingleImage(null)}
                      className="absolute top-2 right-2 p-1 bg-white/90 rounded-full shadow-md hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-red-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-2 flex text-md text-gray-600">
                      <label
                        htmlFor="fileInput"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="fileInput"
                          type="file"
                          className="sr-only"
                          onChange={handleFileChange}
                          accept="image/*, .heic, .heif"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 flex justify-start space-x-4">
            {/* <button
              type="button"
              onClick={() => window.history.back()}
              className="px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button> */}
            <button
              type="submit"
              disabled={isButtonLoading}
              className={`px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isButtonLoading ? "opacity-75 cursor-not-allowed" : ""
              }`}
            >
              {isButtonLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                "Update Testimonial"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestimonialCollectionDetails;